import { ArrowRight } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import type { ExploreWorkflowCard } from "../types";

interface WorkflowCardProps {
  workflow: ExploreWorkflowCard;
  exploreBase: string;
}

export function WorkflowCard({ workflow, exploreBase }: WorkflowCardProps) {
  return (
    <Link
      className="group hover:-translate-y-0.5 flex h-full flex-col rounded-[20px] p-5 no-underline shadow-[var(--explore-workflow-card-shadow)] transition-all hover:shadow-[var(--explore-workflow-card-shadow-hover)]"
      href={`${exploreBase}/stories/${workflow.id}`}
      prefetch={false}
      style={{
        background: "var(--explore-workflow-card-bg)",
        fontFamily: "var(--homepage-sans-stack)",
      }}
    >
      <span className="mb-3 inline-flex w-fit rounded-full border-[1.5px] border-[var(--explore-border)] px-3 py-1 font-semibold text-[0.6rem] text-[var(--explore-text-muted)] uppercase tracking-[0.08em]">
        Today&apos;s Pick
      </span>

      {/* Tool source */}
      <div className="mb-3 flex items-center gap-2.5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-[10px] bg-[var(--explore-bg-card)] shadow-[var(--explore-inline-shadow)]">
          {workflow.toolIconUrl ? (
            <Image
              alt={workflow.toolName}
              className="h-full w-full object-cover"
              height={40}
              src={workflow.toolIconUrl}
              width={40}
            />
          ) : (
            <span className="text-xl">{workflow.toolEmoji}</span>
          )}
        </div>
        <div>
          <h4 className="font-semibold text-[0.9rem] text-[var(--explore-text-primary)] leading-tight">
            {workflow.toolName}
          </h4>
          <span className="text-[0.75rem] text-[var(--explore-text-muted)]">
            {workflow.toolDate}
          </span>
        </div>
      </div>

      {/* Title + desc */}
      <h3
        className="mb-2 line-clamp-2 font-semibold text-[1.25rem] text-[var(--explore-text-primary)] leading-[1.3]"
        style={{ fontFamily: "var(--explore-card-title-stack)" }}
      >
        {workflow.title}
      </h3>
      <p className="mb-3 line-clamp-2 text-[0.9rem] text-[var(--explore-text-secondary)] leading-[1.55]">
        {workflow.description}
      </p>

      {/* Footer */}
      <div className="mt-auto flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap gap-1.5">
          {workflow.tags.slice(0, 3).map((tag) => (
            <span
              className="min-w-0 truncate rounded-sm bg-[var(--explore-tag-bg)] px-3 py-1 font-medium text-[0.8rem] text-[var(--explore-tag-text)]"
              key={tag.slug}
            >
              {tag.displayName}
            </span>
          ))}
        </div>
        <span className="flex items-center gap-1.5 font-semibold text-[0.85rem] text-[var(--explore-accent-orange)]">
          Read more
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-[3px]" />
        </span>
      </div>
    </Link>
  );
}
